import { Box, Flex, Text } from '@chakra-ui/react';
import {
  Banner,
  Checkbox,
  FormFeedback,
  OutlineCard,
  Radio
} from '@morressier/alembic';
import { FormFieldPublishingModel } from '@publishing-engine/domain/types/article';
import { useFormContext } from 'react-hook-form';
import { FieldLabel } from 'src/components/Field';
import { ISubmissionFormData } from 'src/contexts/FormContext';
import { get } from 'lodash';

const PublishingModel = ({
  config,
  children
}: {
  config: FormFieldPublishingModel;
  children?: React.ReactNode;
}) => {
  const {
    formState: { errors },
    register,
    watch
  } = useFormContext<ISubmissionFormData>();
  const {
    params: { validations, labels },
    caseDataFields: { main: field, dependant }
  } = config;
  const mainField = `${field}.model` as const;
  const dependantField = `${field}.${dependant}` as const;
  const error = get(errors, field);

  const publishingPreference = watch(mainField);

  const dependantError = get(error, dependant);

  return (
    <Flex flexDirection="column" gap={8}>
      <Flex flexDirection="column" gap={2}>
        <Text textStyle="headingSection">{labels.section.title}</Text>
        <Text textStyle="textBody">{labels.section.explanation}</Text>
      </Flex>

      <OutlineCard padding={8} borderless width="100%">
        <Flex flexDirection="column" gap={4}>
          <FieldLabel
            isRequired={validations.required}
            label={labels.main}
            subText={labels.subtitle}
            helpText={labels.helpText}
          />

          {['subscription', 'openAccess'].map(key => (
            <Radio
              key={key}
              {...register(mainField)}
              value={key}
              ariaLabel={key}
              id={key}
            >
              <Flex flexDirection="column">
                <Text textStyle="headingItem">{labels.options[key]?.main}</Text>
                <Text>{labels.options[key]?.secondary}</Text>
              </Flex>
            </Radio>
          ))}

          {error?.model ? (
            <FormFeedback variant="error">
              Indicate your publishing preference before proceeding.
            </FormFeedback>
          ) : null}
        </Flex>
      </OutlineCard>

      <Box>
        {publishingPreference === 'openAccess' && (
          <OutlineCard padding={8} borderless width="100%">
            <Flex flexDirection="column" gap={4}>
              <FieldLabel
                isRequired
                label="Article Publication Charge"
                subText="In order to publish your work as Open Access, you must acknowledge that an Article Publication Charge may be payable if your article is accepted."
                helpText={labels.helpText}
              />

              <Checkbox
                ariaLabel="Acknowledge Publication Charge"
                id="acknowledgeCharge"
                {...register(dependantField)}
                isInvalid={Boolean(dependantError)}
              >
                I acknowledge that an Article Publication Charge may be payable
                if my article is accepted.
              </Checkbox>

              <Banner
                variant="attention"
                heading="About Open Access"
                content={
                  <Text>
                    With Open Access, your work will be published under{' '}
                    {labels.options[publishingPreference]?.main}. By doing so,
                    your work would be freely available to a wider audience,
                    which can increase the visibility and impact of your
                    research.
                    <br />
                    <br /> An article publication charge (APC) may be payable if
                    your work is accepted, but waivers or discounts can be
                    applied if your institution or country has an agreement that
                    covers this journal.
                  </Text>
                }
              />

              {dependantError ? (
                <FormFeedback variant="error">
                  Please acknowledge the Article Processing Charge to proceed.
                </FormFeedback>
              ) : null}
            </Flex>
          </OutlineCard>
        )}
        {children}
      </Box>
    </Flex>
  );
};

export default PublishingModel;
